import { FC } from 'react';
import { Award } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import type { TherapistProfile } from '../../../../types/therapist-dashboard.types';

export interface CertificationsListProps {
  certifications: TherapistProfile['certifications'];
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export const CertificationsList: FC<CertificationsListProps> = ({ certifications }) => {
  if (!certifications || certifications.length === 0) {
    return (
      <p className="text-gray-500 dark:text-slate-400 text-sm italic">
        No certifications added
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {certifications.map((cert, index) => {
        const isExpired = cert.expiryDate && new Date(cert.expiryDate) < new Date();
        const isExpiringSoon =
          cert.expiryDate &&
          !isExpired &&
          new Date(cert.expiryDate) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);

        return (
          <div
            key={`${cert.name}-${index}`}
            className={cn(
              'p-3 rounded-lg border',
              isExpired
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : isExpiringSoon
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                  : 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700'
            )}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-slate-100">{cert.name}</p>
                <p className="text-sm text-gray-600 dark:text-slate-400">{cert.issuer}</p>
                <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                  Issued: {formatDate(cert.date)}
                  {cert.expiryDate && ` | Expires: ${formatDate(cert.expiryDate)}`}
                </p>
              </div>
              <Award
                className={cn(
                  'w-5 h-5 flex-shrink-0',
                  isExpired
                    ? 'text-red-500'
                    : isExpiringSoon
                      ? 'text-amber-500'
                      : 'text-green-500'
                )}
              />
            </div>
            {isExpired && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">
                This certification has expired
              </p>
            )}
            {isExpiringSoon && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
                Expiring soon - please renew
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};
